'use client';

import { Box, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import { DonutBlock, InfoDotTooltip, RealEstateRiskHistoryChart, SectionTable } from './TickerPortfolioTabVisuals';
import type { PortfolioTabViewModel, SectionId } from './TickerPortfolioTabClient';

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type Props = {
  data: PortfolioTabViewModel;
  activeSection: SectionId;
  chartColors: string[];
  isDark: boolean;
  contractDonutBackground: string;
  groupCount: (labels: string[]) => Array<{ label: string; value: number }>;
  groupSum: (items: Array<{ label: string; value: number | null }>) => Array<{ label: string; value: number }>;
  formatOptionalPercent: (value: number | null) => string;
};

export function PortfolioRealEstateSection({
  data,
  activeSection,
  chartColors,
  isDark,
  contractDonutBackground,
  groupCount,
  groupSum,
  formatOptionalPercent,
}: Props) {
  const colors = useAppColors();
  const geographicStateExposure = data.geographicStateExposure;
  const geographicRegionExposure = data.geographicRegionExposure;
  if (data.realEstate.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem imóveis diretos reportados na CVM para o período mais recente.
      </Typography>
    );
  }

  const vacancyGroups = groupCount(
    data.realEstate.map((item) =>
      item.vacancyPercent === null ? 'Sem dado' : item.vacancyPercent > 0 ? 'Com vacância' : 'Sem vacância',
    ),
  );
  const delinquencyGroups = groupCount(
    data.realEstate.map((item) =>
      item.delinquencyPercent === null
        ? 'Sem dado'
        : item.delinquencyPercent > 0
          ? 'Com inadimplência'
          : 'Sem inadimplência',
    ),
  );
  const propertyCountByState =
    geographicStateExposure?.states.map((state) => ({
      label: state.stateCode === 'unknown' || state.stateCode === 'unmapped' ? state.stateLabel : state.stateCode,
      value: state.propertyCount,
    })) ?? [];
  const propertyCountByRegion =
    geographicRegionExposure?.states.map((region) => ({
      label: region.stateLabel,
      value: region.propertyCount,
    })) ?? [];
  const revenueByAsset = groupSum(
    data.realEstate.map((item) => ({
      label: item.name,
      value: item.revenuePercent,
    })),
  ).slice(0, 8);
  const vacancyContributionByAsset = data.realEstate
    .map((item) => {
      const revenue = item.revenuePercent ?? 0;
      const vacancy = item.vacancyPercent ?? 0;
      return {
        name: item.name,
        revenuePercent: item.revenuePercent,
        contributionPercentPoints: (revenue * vacancy) / 100,
      };
    })
    .filter((item) => item.contributionPercentPoints > 0)
    .sort((a, b) => b.contributionPercentPoints - a.contributionPercentPoints)
    .slice(0, 10);
  const delinquencyContributionByAsset = data.realEstate
    .map((item) => {
      const revenue = item.revenuePercent ?? 0;
      const delinquency = item.delinquencyPercent ?? 0;
      return {
        name: item.name,
        revenuePercent: item.revenuePercent,
        contributionPercentPoints: (revenue * delinquency) / 100,
      };
    })
    .filter((item) => item.contributionPercentPoints > 0)
    .sort((a, b) => b.contributionPercentPoints - a.contributionPercentPoints)
    .slice(0, 10);
  const realEstateView =
    activeSection === 'realEstateRisk' ? 'risk' : activeSection === 'realEstateContracts' ? 'contracts' : 'composition';
  const contractPanel =
    data.contractBreakdown.length > 0 ? (
      <Paper variant='outlined' sx={{ p: 1.25 }}>
        <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
          <Typography variant='subtitle2' fontWeight={700}>
            Contratos de locação por características (CVM)
          </Typography>
          <InfoDotTooltip text='Base CVM de imóveis de renda acabados e/ou texto agregado do informe trimestral complementar.' />
        </Stack>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
          Referente a contratos de locação ligados a imóveis do fundo.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
            gap: 1.2,
            alignItems: 'start',
          }}
        >
          <Stack spacing={1} alignItems='center'>
            <Box
              sx={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                background: contractDonutBackground,
                display: 'grid',
                placeItems: 'center',
                boxShadow: isDark ? 'inset 0 0 0 1px hsl(var(--border) / 0.9)' : 'inset 0 0 0 1px hsl(var(--border))',
              }}
            >
              <Box
                sx={{
                  width: 98,
                  height: 98,
                  borderRadius: '50%',
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Grupos
                </Typography>
                <Typography variant='subtitle2' fontWeight={800}>
                  {NUMBER_FORMATTER.format(data.contractBreakdown.length)}
                </Typography>
              </Box>
            </Box>
          </Stack>
          <Stack spacing={0.8} sx={{ minWidth: 0 }}>
            {data.contractBreakdown.map((item, index) => (
              <Stack key={`${item.label}-${index}`} spacing={0.35} sx={{ minWidth: 0 }}>
                <Stack direction='row' justifyContent='space-between' spacing={1}>
                  <Stack direction='row' spacing={0.75} alignItems='center' sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: chartColors[index % chartColors.length],
                      }}
                    />
                    <Tooltip title={item.label} arrow>
                      <Typography
                        variant='body2'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Typography variant='body2' fontWeight={700}>
                    {PERCENT_FORMATTER.format(item.percent)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant='determinate'
                  value={Math.max(0, Math.min(100, item.percent))}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: 'hsl(var(--muted))',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: chartColors[index % chartColors.length],
                    },
                  }}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
        <Box sx={{ mt: 1 }}>
          <SectionTable
            headers={['Origem', 'Características contratuais (CVM)']}
            rows={data.contracts.map((item) => [item.source, item.characteristics])}
          />
        </Box>
      </Paper>
    ) : (
      <Paper variant='outlined' sx={{ p: 1.25 }}>
        <Typography variant='body2' color='text.secondary'>
          Sem contratos de locação detalhados na base mais recente.
        </Typography>
      </Paper>
    );

  return (
    <Stack spacing={1.5}>
      {realEstateView === 'composition' ? (
        <>
          {geographicStateExposure?.states.length || geographicRegionExposure?.states.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.2,
              }}
            >
              <DonutBlock
                title='Distribuição por UF'
                items={propertyCountByState}
                colors={chartColors}
                infoText='Distribuição dos imóveis por estado (UF), com base no endereço informado na CVM.'
              />
              <DonutBlock
                title='Distribuição por região'
                items={propertyCountByRegion}
                colors={chartColors}
                infoText='Distribuição dos imóveis por macrorregião do Brasil.'
              />
            </Box>
          ) : null}
          {revenueByAsset.length > 0 ? (
            <DonutBlock
              title='Participação por % receita (top imóveis)'
              items={revenueByAsset}
              colors={chartColors}
              valueKind='percentPoints'
              infoText='Participação dos principais imóveis na receita do fundo.'
            />
          ) : null}
          {data.realEstate.length > 0 ? (
            <SectionTable
              headers={['Imóvel', 'Endereço', '% Receita']}
              rows={data.realEstate.map((item) => [
                item.name,
                item.address ?? '-',
                formatOptionalPercent(item.revenuePercent),
              ])}
            />
          ) : null}
        </>
      ) : null}

      {realEstateView === 'risk' ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1.2,
            }}
          >
            <DonutBlock
              title='Vacância (imóveis)'
              items={vacancyGroups}
              colors={chartColors}
              infoText='Mostra quantos imóveis estão com vacância reportada no período.'
            />
            <DonutBlock
              title='Inadimplência (imóveis)'
              items={delinquencyGroups}
              colors={chartColors}
              infoText='Mostra quantos imóveis têm inadimplência reportada no período.'
            />
          </Box>
          {data.realEstateHistory.length > 1 ? (
            <Paper variant='outlined' sx={{ p: 1.25 }}>
              <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='subtitle2' fontWeight={700}>
                  Histórico de risco locatário
                </Typography>
                <InfoDotTooltip text='Séries trimestrais da CVM para receita (%), vacância, inadimplência e ocupação estimada (100 - vacância).' />
              </Stack>
              <RealEstateRiskHistoryChart points={data.realEstateHistory} />
            </Paper>
          ) : null}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1.2,
            }}
          >
            <Paper variant='outlined' sx={{ p: 1.25 }}>
              <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='subtitle2' fontWeight={700}>
                  Imóveis que mais contribuem para vacância
                </Typography>
                <InfoDotTooltip text='Contribuição estimada = % receita do imóvel x % vacância do imóvel.' />
              </Stack>
              {vacancyContributionByAsset.length > 0 ? (
                <Stack spacing={0.8}>
                  {vacancyContributionByAsset.map((item, index) => {
                    const max = vacancyContributionByAsset[0]?.contributionPercentPoints ?? 1;
                    const pct = max > 0 ? (item.contributionPercentPoints / max) * 100 : 0;
                    return (
                      <Stack key={`vacancy-contrib-${item.name}-${index}`} spacing={0.35}>
                        <Stack direction='row' justifyContent='space-between' spacing={1}>
                          <Typography variant='body2'>
                            {item.name}{' '}
                            <Typography component='span' variant='caption' color='text.secondary'>
                              ({formatOptionalPercent(item.revenuePercent)} da receita)
                            </Typography>
                          </Typography>
                          <Typography variant='body2' fontWeight={700} color='warning.main'>
                            {PERCENT_FORMATTER.format(item.contributionPercentPoints)} pp
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant='determinate'
                          value={Math.max(0, Math.min(100, pct))}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: 'hsl(var(--muted))',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: colors.semantic.warning,
                            },
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Sem contribuição relevante de vacância no período.
                </Typography>
              )}
            </Paper>
            <Paper variant='outlined' sx={{ p: 1.25 }}>
              <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='subtitle2' fontWeight={700}>
                  Imóveis que mais contribuem para inadimplência
                </Typography>
                <InfoDotTooltip text='Contribuição estimada = % receita do imóvel x % inadimplência do imóvel.' />
              </Stack>
              {delinquencyContributionByAsset.length > 0 ? (
                <Stack spacing={0.8}>
                  {delinquencyContributionByAsset.map((item, index) => {
                    const max = delinquencyContributionByAsset[0]?.contributionPercentPoints ?? 1;
                    const pct = max > 0 ? (item.contributionPercentPoints / max) * 100 : 0;
                    return (
                      <Stack key={`delinquency-contrib-${item.name}-${index}`} spacing={0.35}>
                        <Stack direction='row' justifyContent='space-between' spacing={1}>
                          <Typography variant='body2'>
                            {item.name}{' '}
                            <Typography component='span' variant='caption' color='text.secondary'>
                              ({formatOptionalPercent(item.revenuePercent)} da receita)
                            </Typography>
                          </Typography>
                          <Typography variant='body2' fontWeight={700} color='error.main'>
                            {PERCENT_FORMATTER.format(item.contributionPercentPoints)} pp
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant='determinate'
                          value={Math.max(0, Math.min(100, pct))}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: 'hsl(var(--muted))',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: colors.semantic.dangerStrong,
                            },
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Sem contribuição relevante de inadimplência no período.
                </Typography>
              )}
            </Paper>
          </Box>
          <SectionTable
            headers={['Imóvel', 'Endereço', 'Vacância', 'Inadimplência', '% Receita']}
            rows={data.realEstate.map((item) => [
              item.name,
              item.address ?? '-',
              formatOptionalPercent(item.vacancyPercent),
              formatOptionalPercent(item.delinquencyPercent),
              formatOptionalPercent(item.revenuePercent),
            ])}
          />
        </>
      ) : null}

      {realEstateView === 'contracts' ? contractPanel : null}
    </Stack>
  );
}
